import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";

export interface FocusedDocumentOptions { inspectorWidth: number; railWidth: number; gap: number }

export function solveDocumentCanvas(viewportWidth: number, canonicalWidth: number, maxRenderedWidth: number, workspaceReserve: number, outerGutter = 32) {
  const renderedWidth = Math.min(maxRenderedWidth, Math.max(0, viewportWidth - outerGutter - workspaceReserve));
  return { renderedWidth, scale: renderedWidth / canonicalWidth };
}

export function useDocumentCanvas(viewportWidthFallback: number, canonicalWidth: number, maxRenderedWidth: number, workspaceReserve: number, container?: HTMLElement | null) {
  const [canvas, setCanvas] = useState(() => solveDocumentCanvas(typeof window === "undefined" ? viewportWidthFallback : window.innerWidth, canonicalWidth, maxRenderedWidth, workspaceReserve));
  useLayoutEffect(() => {
    const measure = () => {
      // Detail pages already have navigation and gutters outside their paper stage.
      const style = container ? getComputedStyle(container) : null;
      const availableWidth = container ? container.getBoundingClientRect().width - parseFloat(style!.paddingLeft) - parseFloat(style!.paddingRight) : window.innerWidth;
      if (availableWidth <= 0) return;
      setCanvas(current => {
        const next = solveDocumentCanvas(availableWidth, canonicalWidth, maxRenderedWidth, workspaceReserve, container ? 0 : 32);
        return current.renderedWidth === next.renderedWidth ? current : next;
      });
    };
    measure();
    if (container) {
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [canonicalWidth, maxRenderedWidth, workspaceReserve, container]);
  return canvas;
}

export interface FocusedDocumentLayout {
  availableWidth: number;
  documentWidth: number;
  inspectorWidth: number;
  expandedInspectorWidth: number;
  gap: number;
  scale: number;
  collapsed: boolean;
  canExpand: boolean;
}

export function solveFocusedDocumentLayout(availableWidth: number, renderedDocumentWidth: number, canonicalWidth: number, options: FocusedDocumentOptions, forceCollapsed = false): FocusedDocumentLayout {
  const spaceBesideDocument = Math.max(0, (availableWidth - renderedDocumentWidth) / 2);
  const canExpand = spaceBesideDocument >= options.gap + options.inspectorWidth;
  const collapsed = forceCollapsed || !canExpand;
  const inspectorWidth = collapsed ? Math.min(options.railWidth, Math.max(0, spaceBesideDocument - options.gap)) : options.inspectorWidth;
  const documentWidth = renderedDocumentWidth;
  return { availableWidth, documentWidth, inspectorWidth, expandedInspectorWidth: options.inspectorWidth, gap: options.gap, scale: documentWidth / canonicalWidth, collapsed, canExpand };
}

export function useFocusedDocumentLayout(containerRef: RefObject<HTMLElement | null>, renderedDocumentWidth: number, canonicalWidth: number, options: FocusedDocumentOptions, enabled = true, forceCollapsed = false) {
  const [layout, setLayout] = useState(() => {
    const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
    return solveFocusedDocumentLayout(viewportWidth - 32, renderedDocumentWidth, canonicalWidth, options, forceCollapsed);
  });
  useLayoutEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const style = getComputedStyle(container);
      const availableWidth = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      setLayout(current => {
        const next = solveFocusedDocumentLayout(availableWidth, renderedDocumentWidth, canonicalWidth, options, forceCollapsed);
        return current.collapsed === next.collapsed && current.documentWidth === next.documentWidth && current.inspectorWidth === next.inspectorWidth && current.availableWidth === next.availableWidth ? current : next;
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, renderedDocumentWidth, canonicalWidth, options, enabled, forceCollapsed]);
  return layout;
}

export function useFocusedDocumentInspector(collapsed: boolean, triggerRef: RefObject<HTMLButtonElement | null>) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [collapsed]);
  useEffect(() => {
    if (!collapsed || !open) return;
    const onEscape = (event: KeyboardEvent) => { if (event.key === "Escape") { setOpen(false); triggerRef.current?.focus(); } };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [collapsed, open, triggerRef]);
  return { open, toggle: () => setOpen(current => !current) };
}

export function FocusedDocumentWorkbench({ commandBar, canvas, inspector, layout, className = "", style, canvasBusy = false }: { commandBar: ReactNode; canvas: ReactNode; inspector: ReactNode; layout: FocusedDocumentLayout; className?: string; style?: CSSProperties; canvasBusy?: boolean }) {
  const commandBarRef = useRef<HTMLDivElement>(null);
  const [commandBarHeight, setCommandBarHeight] = useState(48);
  useLayoutEffect(() => {
    const commandBarElement = commandBarRef.current;
    if (!commandBarElement) return;
    const measure = () => setCommandBarHeight(commandBarElement.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(commandBarElement);
    return () => observer.disconnect();
  }, []);
  return <div className={`focused-document-workbench ${className}`} style={{ ...style, "--focused-available-width": `${layout.availableWidth}px`, "--focused-document-width": `${layout.documentWidth}px`, "--focused-inspector-width": `${layout.inspectorWidth}px`, "--focused-expanded-inspector-width": `${layout.expandedInspectorWidth}px`, "--focused-workbench-gap": `${layout.gap}px` } as CSSProperties}>
    <div className="focused-document-anchor" style={{ "--focused-commandbar-height": `${commandBarHeight}px` } as CSSProperties}>
    <div className="focused-document-column">
      <div ref={commandBarRef} className="focused-document-commandbar">{commandBar}</div>
      <main className="focused-document-canvas paper-stage" inert={canvasBusy} aria-busy={canvasBusy || undefined}>{canvas}</main>
    </div>
    {inspector ? <div className="focused-document-inspector">{inspector}</div> : null}
    </div>
  </div>;
}
